import moment from 'moment'
import { MidiStats, RoomContextState } from '../../RoomContext'
import {
    MuseRtcMediaTrackStats,
    RtcStatsCandidatePairType,
    RtcStatsCandidatePairTypeExt,
    RtcStatsInboundRtpType,
    RtcStatsMediaSourceType,
    RtcStatsReportCollection as RtcStatsReportObject,
} from './RtcStatsTypes'
import { MuseRtcStats, SystemResourceUsageStats, SystemStats, systemStatsEmitter } from './SystemStats'
import { logger } from '../../services/Logger'
import { serializeError } from 'serialize-error'
import ElectronIPC from '../../services/ElectronIPC'
import { MusePeer } from '../../services/RoomContextMusePeer'
import { AudioInputStreamInfo } from '../../UserContext'
import { AudioEngineNetworkStats } from '../../core/AudioEngine'
import { HostPingMeasurement } from '../../services/usePeerPingMeasurement'

const log = logger.createChildLogger({ name: 'RtcStats' })

type Query = {
    [key: string]: any
}

type RtcStatDatapointForRemote = {
    networkType: 'p2p' | 'sfu'

    // the overall RTC connection status object
    rtcOverviewStats: RtcStatsCandidatePairType | null

    // the array of media track statistics, organized by user and media id.
    mediaTrackStats: RtcStatDatapointForRemoteMediaTrackStats[]

    // cpu/ram usage stats
    systemResourceUsageStats: SystemResourceUsageStats | null

    // ping measurements for each peer
    pingMeasurement: HostPingMeasurement | null

    // audio engine network stats
    audioEngineNetworkStats: AudioEngineNetworkStats

    audioEnginePerStreamReceptionStats: any

    midiStats: MidiStats
}

type RtcStatDatapointForRemoteMediaTrackStats = MuseRtcMediaTrackStats & {
    userUrn: string
    username: string
    sessionId: string
    trackId: string
    trackLabel: string
}

export class RtcStatsUtils {
    static queryReport(report: RtcStatsReportObject, query: Query) {
        for (let doc of report) {
            let isMatch = true
            // the for block below sets isMatch to false if non-match; otherwise,
            // it is a match.
            for (let qk of Object.keys(query)) {
                // @ts-ignore
                if (doc[qk] !== query[qk]) {
                    isMatch = false
                    break
                }
            }

            if (isMatch) {
                // if made it here, then it means that all the k/v of `query`
                // matched the `doc`'s. we found our match.
                return doc
            }
        }

        return null
    }

    static convertReportToObject(r: any) {
        const skippedKeys = ['RTCCertificate_', 'RTCCodec_', 'RTCMediaStream_']
        const flattenValues = (report: any) => {
            if (Object.prototype.toString.call(report.values) === '[object Array]') {
                report.values.forEach((value: any) => {
                    Object.assign(report, value)
                })
            }
            return report
        }

        const reports: RtcStatsReportObject = []
        r.forEach((report: any) => {
            if (skippedKeys.find((k) => (report.id || '').startsWith(k))) {
                return
            }
            reports.push(flattenValues(report))
        })

        return reports
    }

    static extractOverviewStatsFromReport(report: RtcStatsReportObject) {
        if (report.length === 0) {
            return null
        }

        const doc = RtcStatsUtils.queryReport(report, {
            type: 'candidate-pair',
            state: 'succeeded',
        }) as RtcStatsCandidatePairType | null

        return doc
    }

    static lastLoggedTime = moment(0)
    static prevSystemStats: SystemStats = {
        rtcOverviewStats: null,
        mediaTrackStats: null,
        systemResourceUsageStats: null,
        pingMeasurement: null,
        audioEngineNetworkStats: { serverPing: null },
        audioEnginePerStreamReceptionStats: null,
        midiStats: { txCount: 0, rxCount: 0 },
    }
    static async setStats(
        roomState: RoomContextState,
        pingMeasurement: HostPingMeasurement | null,
        audioEngineNetworkStats: AudioEngineNetworkStats,
        audioEnginePerStreamReceptionStats: any,
        midiStats: MidiStats
    ) {
        try {
            let rtcStats: MuseRtcStats | null = null

            // WebRTC stats
            if (roomState.expectedNetworkTopology === 'p2p') {
                rtcStats = await this._extractStatsForP2p(roomState)
            } else {
                // otherwise, if SFU: (WIP)
                rtcStats = (await this._extractStatsForSfu(roomState)) ?? null
            }

            // we calculate `calculatedBytesReceivedPerSecond` and `calculatedBytesSentPerSecond`
            // for `rtcStats.rtcOverviewStats`.
            let rtcOverviewStatsExt: RtcStatsCandidatePairTypeExt | null = null
            if (rtcStats?.rtcOverviewStats) {
                if (this.prevSystemStats?.rtcOverviewStats) {
                    let bytesSentDelta =
                        (rtcStats.rtcOverviewStats.bytesSent || 0) -
                        (this.prevSystemStats.rtcOverviewStats.bytesSent || 0)
                    let bytesReceivedDelta =
                        (rtcStats.rtcOverviewStats.bytesReceived || 0) -
                        (this.prevSystemStats.rtcOverviewStats.bytesReceived || 0)
                    let deltaT = rtcStats.rtcOverviewStats.timestamp - this.prevSystemStats.rtcOverviewStats.timestamp

                    rtcOverviewStatsExt = {
                        ...rtcStats.rtcOverviewStats,
                        calculatedBytesSentPerSecond: Math.floor(bytesSentDelta / (deltaT / 1000)),
                        calculatedBytesReceivedPerSecond: Math.floor(bytesReceivedDelta / (deltaT / 1000)),
                    }
                } else {
                    rtcOverviewStatsExt = {
                        ...rtcStats.rtcOverviewStats,
                        calculatedBytesSentPerSecond: 0,
                        calculatedBytesReceivedPerSecond: 0,
                    }
                }
            } else {
                rtcOverviewStatsExt = null
            }

            // CPU / RAM stats
            // NOTE: will be null if on the browser.
            const systemResourceUsageStats = await ElectronIPC.getSystemResourceUsageStats()

            const systemStats: SystemStats = {
                rtcOverviewStats: rtcOverviewStatsExt,
                mediaTrackStats: rtcStats?.mediaTrackStats || null,
                systemResourceUsageStats: systemResourceUsageStats,
                pingMeasurement,
                audioEngineNetworkStats,
                audioEnginePerStreamReceptionStats,
                midiStats,
            }

            // set the global reference to the statistics
            systemStatsEmitter.write(systemStats)

            this.prevSystemStats = systemStats

            // convert systemStats to a format that the server likes and then log.
            // (RtcStatDatapointForRemote for mediaTrackStats)
            if (moment().diff(this.lastLoggedTime, 'seconds') >= 60) {
                const r = this._convertSystemStatsForRemoteLogging(systemStats, roomState)
                if (r) {
                    log.info(`periodicStats`, `periodic stats`, r)
                    this.lastLoggedTime = moment()
                }
            }
        } catch (e) {
            log.assert(
                'periodicStats',
                'caught unexpected exception at periodicStats. ' + 'This should never happen!',
                { e: serializeError(e) }
            )
        }
    }

    static _convertSystemStatsForRemoteLogging(
        systemStats: SystemStats,
        roomState: RoomContextState
    ): RtcStatDatapointForRemote {
        // basically, convert the data structure of `systemStats.mediaTrackStats` to one that the server likes.
        let mediaTrackStatsConverted: RtcStatDatapointForRemoteMediaTrackStats[] = []
        Object.keys(systemStats.mediaTrackStats || {}).forEach((trackId) => {
            if (!systemStats.mediaTrackStats) {
                return
            }

            let obj = systemStats.mediaTrackStats[trackId] || null
            if (obj) {
                mediaTrackStatsConverted.push({
                    ...obj,
                    ...obj.srcInfo,
                } as RtcStatDatapointForRemoteMediaTrackStats)
            }
        })

        // remove stale stats (see timestamp)
        let { audioEnginePerStreamReceptionStats = {} } = systemStats
        Object.keys(audioEnginePerStreamReceptionStats || {}).forEach((streamUuid) => {
            const s = audioEnginePerStreamReceptionStats[streamUuid]
            let d = new Date().getTime() - (s?.timestamp || 0)
            if (d > 60000) {
                audioEnginePerStreamReceptionStats[streamUuid] = null
            }
        })

        return {
            networkType: roomState.expectedNetworkTopology || 'p2p',
            rtcOverviewStats: systemStats.rtcOverviewStats,
            mediaTrackStats: mediaTrackStatsConverted,
            systemResourceUsageStats: systemStats.systemResourceUsageStats,
            pingMeasurement: systemStats.pingMeasurement,
            audioEngineNetworkStats: systemStats.audioEngineNetworkStats,
            audioEnginePerStreamReceptionStats,
            midiStats: systemStats.midiStats,
        }
    }

    static async _extractStatsForP2p(roomState: RoomContextState) {
        let result: MuseRtcStats = {
            rtcOverviewStats: null,
            mediaTrackStats: null,
        }

        // for p2p, there's some weirdness because there can theoretically be multiple remote peers,
        // but we can have only one rtcOverviewStats. we just take the "most valid" rtcOverviewStats
        // (last one in the array for now, out of convenience).
        for (let peerSessionId of Object.keys(roomState.peers)) {
            let peer = roomState.peers[peerSessionId]
            if (!peer.p2pConnection) {
                continue
            }

            try {
                // @ts-ignore
                const rtcStatsReport: RTCStatsReport = await peer.p2pConnection._pc.getStats()
                const rtcStatsObject = RtcStatsUtils.convertReportToObject(rtcStatsReport)

                const rtcOverviewStats = RtcStatsUtils.extractOverviewStatsFromReport(rtcStatsObject)

                /*** fill in the object ***/
                if (rtcOverviewStats?.state === 'succeeded') {
                    result.rtcOverviewStats = rtcOverviewStats
                }
                peer.remoteP2pAudios.forEach((track) => {
                    try {
                        if (result.mediaTrackStats == null) {
                            result.mediaTrackStats = {}
                        }
                        result.mediaTrackStats[track.track.id] = RtcStatsUtils.extractMediaTrackStatsFromReport(
                            rtcStatsObject,
                            track.track,
                            track.info,
                            'p2p',
                            peer
                        )
                    } catch (e) {
                        log.error(`periodicStats`, `caught exception while handling the P2P case:`, {
                            e: serializeError(e),
                        })
                    }
                })
            } catch (e) {
                log.error(`periodicStats`, `caught exception while handling the P2P case:`, { e: serializeError(e) })
            }
        }

        return result
    }

    static async _extractStatsForSfu(roomState: RoomContextState) {
        let result: MuseRtcStats = {
            rtcOverviewStats: null,
            mediaTrackStats: null,
        }

        if (!roomState.sfuRoom) {
            return null
        }

        const getStats = roomState.sfuRoom.engine?.pcManager?.subscriber?.getStats
        if (!getStats || typeof getStats !== 'function') {
            return
        }

        try {
            const rtcStatsReport = await roomState.sfuRoom.engine?.pcManager?.subscriber?.getStats()
            const rtcStatsObject = RtcStatsUtils.convertReportToObject(rtcStatsReport)

            const rtcOverviewStats = RtcStatsUtils.extractOverviewStatsFromReport(rtcStatsObject)

            /*** 1) fill in the rtcOverviewStats ***/
            result.rtcOverviewStats = rtcOverviewStats

            /*** 2) fill in the mediaTrackStats for each peer ***/
            for (let peerSessionId of Object.keys(roomState.peers)) {
                let peer = roomState.peers[peerSessionId]

                if (peer.manifest?.isAudioDeprioritized || peer.manifest?.clientType === 'listenonly') {
                    continue
                }

                peer.remoteSfuAudios.forEach((track) => {
                    try {
                        if (result.mediaTrackStats == null) {
                            result.mediaTrackStats = {}
                        }
                        result.mediaTrackStats[track.track.id] = RtcStatsUtils.extractMediaTrackStatsFromReport(
                            rtcStatsObject,
                            track.track,
                            track.info,
                            'sfu',
                            peer
                        )
                    } catch (e) {
                        log.error(`periodicStats`, `caught exception while handling the SFU case:`, {
                            e: serializeError(e),
                        })
                    }
                })
            }

            return result
        } catch (e) {
            log.error(`periodicStats`, `caught exception while handling the SFU case:`, { e: serializeError(e) })
            return null
        }
    }

    /**
     * get a matching 'inbound-rtp' and 'remote-outbound-rtp' for an incoming MediaStreamTrack:
     *
     * Algorithm:
     * 1. find document by trackIdentifier =? track.id
     * {
     *   type: 'track'
     *   trackIdentifier: *** the track.id ***
     *   mediaSourceId: ==> save this (looks like "RTCAudioSource_2")
     * }
     *
     * 2. find document by matching trackId === {previous doc's id}.
     * {
     *    type: 'inbound-rtp'
     *    trackId: *** the media-source id saved above ***
     *    id: ==> save this
     * }
     *
     * 3. find document by matching localId === {previous doc's id}.
     * {
     *    type: 'remote-outbound-rtp'
     *    localId: *** the inbound-rtp id saved above ***
     * }
     */
    static extractMediaTrackStatsFromReport(
        report: RtcStatsReportObject,
        track: MediaStreamTrack,
        info: AudioInputStreamInfo,
        networkType: 'p2p' | 'sfu',
        peer: MusePeer
    ): MuseRtcMediaTrackStats | null {
        if (report.length === 0) {
            return null
        }

        let inboundRtpDoc = (function method1() {
            let trackDoc = RtcStatsUtils.queryReport(report, {
                type: 'track',
                trackIdentifier: track.id,
            }) as RtcStatsMediaSourceType | null

            if (!trackDoc?.id) {
                return null
            }

            const inboundRtpDoc = RtcStatsUtils.queryReport(report, {
                type: 'inbound-rtp',
                trackId: trackDoc.id,
            }) as RtcStatsInboundRtpType | null

            if (!inboundRtpDoc?.id) {
                return null
            }

            return inboundRtpDoc // this returns out of the closure.
        })()

        // if method1 did not work, try this way. this seems to work for SFU.
        if (inboundRtpDoc == null) {
            inboundRtpDoc = RtcStatsUtils.queryReport(report, {
                type: 'inbound-rtp',
                trackIdentifier: track.id,
            }) as RtcStatsInboundRtpType | null

            // if still not found, then return null.
            if (inboundRtpDoc == null) {
                return null
            }
        }

        // const remoteInboundRtpDoc = RtcStatsUtils.queryReport(report, {
        //     type: 'remote-outbound-rtp',
        //     localId: inboundRtpDoc.id
        // }) as RtcStatsRemoteOutboundRtpType|null

        return {
            audioLevel: inboundRtpDoc.audioLevel,
            bytesReceived: inboundRtpDoc.bytesReceived,
            insertedSamplesForDeceleration: inboundRtpDoc.insertedSamplesForDeceleration,
            jitter: inboundRtpDoc.jitter,
            jitterBufferDelay: inboundRtpDoc.jitterBufferDelay,
            jitterBufferEmittedCount: inboundRtpDoc.jitterBufferEmittedCount,
            packetsDiscarded: inboundRtpDoc.packetsDiscarded,
            packetsLost: inboundRtpDoc.packetsLost,
            packetsReceived: inboundRtpDoc.packetsReceived,
            removedSamplesForAcceleration: inboundRtpDoc.removedSamplesForAcceleration,
            totalAudioEnergy: inboundRtpDoc.totalAudioEnergy,
            // totalRoundTripTime: remoteInboundRtpDoc?.totalRoundTripTime ?? null // always 0 for some reason.
            srcInfo: {
                networkType: networkType,
                userUrn: peer.userUrn,
                username: peer.username,
                sessionId: peer.sessionId,
                trackId: track.id,
                trackLabel: info.label,
            },
        }
    }
}
